// src/pages/TreasurerReport.jsx
// Adds "Preview" for bills:
// - In the Add Entry form (under the file field)
// - In both Income & Expense tables (for saved + unsaved rows)

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import {
  FaArrowLeft,
  FaSearch,
  FaTrash,
  FaFileCsv,
  FaFileExcel,
  FaFilePdf,
  FaSave,
  FaPlus,
  FaRedo,
  FaInbox,
  FaExternalLinkAlt,
  FaBalanceScale,
  FaArrowUp,
  FaArrowDown,
} from "react-icons/fa";
import { compressBillImage, formatBytes } from "../utils/imageCompression";
import { authFetch } from "../utils/api";

const ABSOLUTE_HOST = "https://dev.sliitleo.org";

function readUserFromStorage() {
  try {
    const raw = JSON.parse(localStorage.getItem("user")) || {};
    return { name: "Unknown User", role: "Member", image: "/user.png", ...raw };
  } catch {
    return { name: "Unknown User", role: "Member", image: "/user.png" };
  }
}
const currency = (n) =>
  (Number(n || 0)).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const toJsonSafe = async (res) => {
  const t = await res.text();
  try {
    return JSON.parse(t);
  } catch {
    throw new Error(t.slice(0, 400));
  }
};

const isLikelyImage = (name = "", mime = "") => {
  const n = (name || "").toLowerCase();
  const m = (mime || "").toLowerCase();
  return /\.(png|jpe?g|gif|webp)$/i.test(n) || m.startsWith("image/");
};

export default function TreasurerReport() {
  const params = useParams();
  const projectId = useMemo(() => {
    const raw = params.projectId ?? params.id ?? params.pid ?? null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [params]);

  const user = readUserFromStorage();

  // Project header
  const [projectName, setProjectName] = useState("");
  const [projectPhase, setProjectPhase] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!projectId) return;
    (async () => {
      try {
        const res = await fetch(
          `${ABSOLUTE_HOST}/Backend/project_api.php?action=get_details&id=${projectId}`
        );
        const data = await toJsonSafe(res);
        if (cancelled) return;
        if (data?.success) {
          setProjectName(data.project?.name || `Project ${projectId}`);
          const p = data.project || {};
          const phase =
            Number.isFinite(+p.phase_count) && +p.phase_count > 0 ? +p.phase_count : 1;
          setProjectPhase(phase);
        } else {
          setProjectName(`Project ${projectId}`);
        }
      } catch {
        setProjectName(`Project ${projectId}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Entries
  const [entries, setEntries] = useState([]);
  const [type, setType] = useState("income");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [file, setFile] = useState(null);

  // NEW: preview of the *selected* bill (before adding)
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState("");
  const [compressing, setCompressing] = useState(false);
  const [compressionInfo, setCompressionInfo] = useState(null); // { originalSize, compressedSize }

  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(!!projectId);
  const [savingAll, setSavingAll] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);

  const fileInputRef = useRef(null);

  // Load rows
  useEffect(() => {
    let cancelled = false;
    if (!projectId) {
      setEntries([]);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const res = await authFetch(
          `${ABSOLUTE_HOST}/Backend/finance_api.php?action=list&project_id=${projectId}`
        );
        const data = await toJsonSafe(res);
        if (cancelled) return;
        if (!data?.success) throw new Error(data?.message || "Load failed");
        const mapped = (data.items || []).map((r) => ({
          id: r.id,
          type: r.type,
          amount: Number(r.amount || 0),
          description: r.description || "",
          date: r.txn_date,
          bill_url: r.bill_url || "",
          bill_filename: r.bill_filename || "",
          bill_mime: r.bill_mime || "",
          saved: true,
        }));
        setEntries(mapped);
      } catch (e) {
        console.error("list error:", e);
        alert("Failed to load finance entries.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Revoke selected file preview URL on change/unmount
  useEffect(() => {
    return () => {
      if (selectedPreviewUrl) URL.revokeObjectURL(selectedPreviewUrl);
    };
  }, [selectedPreviewUrl]);

  // Filter/search
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return entries;
    return entries.filter(
      (e) =>
        (e.description || "").toLowerCase().includes(s) ||
        (e.type || "").toLowerCase().includes(s)
    );
  }, [entries, q]);

  // Split lists
  const incomes = useMemo(
    () => filtered.filter((e) => e.type === "income"),
    [filtered]
  );
  const expenses = useMemo(
    () => filtered.filter((e) => e.type === "expense"),
    [filtered]
  );

  const totals = useMemo(() => {
    let inc = 0,
      exp = 0;
    for (const e of filtered) {
      if (e.type === "income") inc += Number(e.amount || 0);
      else if (e.type === "expense") exp += Number(e.amount || 0);
    }
    return { income: inc, expense: exp, net: inc - exp };
  }, [filtered]);

  const resetForm = () => {
    setType("income");
    setAmount("");
    setDescription("");
    setDate(new Date().toISOString().slice(0, 10));
    setFile(null);
    if (selectedPreviewUrl) URL.revokeObjectURL(selectedPreviewUrl);
    setSelectedPreviewUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const addEntry = () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      alert("Amount must be greater than 0");
      return;
    }
    const tempId = Date.now() + Math.random();
    const billLocalUrl = file ? (selectedPreviewUrl || URL.createObjectURL(file)) : "";
    setEntries((prev) => [
      ...prev,
      {
        tempId,
        type,
        amount: amt,
        description: description.trim(),
        date,
        file,
        billLocalUrl,
        bill_filename: file?.name || "",
        bill_mime: file?.type || "",
        saved: false,
      },
    ]);
    // Keep the blob URL used in list; do NOT revoke here if we used selectedPreviewUrl.
    setSelectedPreviewUrl("");
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setType("income");
    setAmount("");
    setDescription("");
    setDate(new Date().toISOString().slice(0, 10));
  };

  const deleteEntry = async (row) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      if (row.saved && projectId) {
        const form = new FormData();
        form.append("id", String(row.id));
        form.append("project_id", String(projectId));
        const res = await authFetch(
          `${ABSOLUTE_HOST}/Backend/finance_api.php?action=delete_entry`,
          { method: "POST", body: form }
        );
        const data = await toJsonSafe(res);
        if (!data?.success) {
          const detail = data?.error || data?.message || "Delete failed";
          throw new Error(detail);
        }
      }
      setEntries((prev) =>
        prev.filter((e) => (row.saved ? e.id !== row.id : e.tempId !== row.tempId))
      );
    } catch (e) {
      console.error("delete error:", e);
      alert(e?.message || "Failed to delete. Please try again.");
    }
  };

  // Save all unsaved rows
  const saveAll = async () => {
    if (!projectId) {
      alert("Missing or invalid project ID in the URL.");
      return;
    }
    const unsaved = entries.filter((e) => !e.saved);
    if (!unsaved.length) {
      alert("Nothing to save.");
      return;
    }
    try {
      setSavingAll(true);
      for (const e of unsaved) {
        const fd = new FormData();
        fd.append("project_id", String(projectId));
        fd.append("type", e.type);
        fd.append("amount", String(e.amount));
        fd.append("description", e.description || "");
        fd.append("txn_date", e.date);
        fd.append("created_by", user?.SLIITID || "");
        if (e.file) fd.append("bill", e.file);

        const res = await authFetch(
          `${ABSOLUTE_HOST}/Backend/finance_api.php?action=add_entry`,
          { method: "POST", body: fd }
        );
        const data = await toJsonSafe(res);
        if (!data?.success) {
          const detail = data?.error || data?.message || "Save failed";
          throw new Error(detail);
        }

        setEntries((prev) =>
          prev.map((row) => {
            if (row.saved) return row;
            if (row.tempId !== e.tempId) return row;
            return {
              id: data.id,
              type: e.type,
              amount: e.amount,
              description: e.description,
              date: e.date,
              bill_url: data.bill_url || "",
              bill_filename: e.file?.name || "",
              bill_mime: e.file?.type || "",
              saved: true,
            };
          })
        );
      }
      alert("All entries saved!");
    } catch (err) {
      console.error("saveAll error:", err);
      alert(`Failed to save all entries.\n${err?.message || "Please try again."}`);
    } finally {
      setSavingAll(false);
    }
  };

  const exportCSV = () => {
    const rows = [
      ["Date", "Type", "Description", "Amount", "Bill Link / File"],
      ...entries.map((e) => [
        e.date,
        e.type,
        e.description,
        e.amount,
        e.saved ? e.bill_url || "" : e.file?.name || "",
      ]),
    ];
    const csv = rows
      .map((r) =>
        r.map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `treasurer_report_project_${projectId ?? "unknown"}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Excel export (kept)
  const exportExcel = () => {
    const esc = (s) =>
      String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    const rowHtml = (e) => {
      const link =
        e.saved && e.bill_url
          ? `<a href="${esc(e.bill_url)}">${esc(e.bill_filename || "Bill link")}</a>`
          : esc(e.bill_filename || e.file?.name || "");
      return `<tr>
        <td>${esc(e.date)}</td>
        <td>${esc(e.description)}</td>
        <td style="mso-number-format:'0.00'; text-align:right;">${Number(
          e.amount || 0
        ).toFixed(2)}</td>
        <td>${link || "—"}</td>
      </tr>`;
    };

    const incomeRows = incomes.map(rowHtml).join("");
    const expenseRows = expenses.map(rowHtml).join("");

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Report</title></head>
<body>
  <h3>${esc(projectName || `Project ${projectId || ""}`)} — Treasurer Report</h3>
  <p><b>Total Income:</b> LKR ${currency(totals.income)} &nbsp; 
     <b>Total Expense:</b> LKR ${currency(totals.expense)} &nbsp; 
     <b>Net:</b> LKR ${currency(totals.net)}</p>

  <h4>Income</h4>
  <table border="1" cellspacing="0" cellpadding="4">
    <tr><th>Date</th><th>Description</th><th>Amount (LKR)</th><th>Bill</th></tr>
    ${incomeRows || '<tr><td colspan="4">—</td></tr>'}
  </table>

  <br>

  <h4>Expense</h4>
  <table border="1" cellspacing="0" cellpadding="4">
    <tr><th>Date</th><th>Description</th><th>Amount (LKR)</th><th>Bill</th></tr>
    ${expenseRows || '<tr><td colspan="4">—</td></tr>'}
  </table>
</body></html>`;

    const blob = new Blob([html], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `treasurer_report_${projectId ?? "report"}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Helpers for PDF export
  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });

  const urlToImageDataUrl = async (url) => {
    try {
      const resp = await fetch(url, { mode: "cors" });
      const blob = await resp.blob();
      if (!blob.type.startsWith("image/")) return null;
      return await new Promise((resolve) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.readAsDataURL(blob);
      });
    } catch {
      return null; // CORS blocked or non-image
    }
  };

  const exportPDF = async () => {
    setExportingPDF(true);
    try {
      let jsPDFmod;
      try {
        jsPDFmod = await import("jspdf");
      } catch {
        alert(
          "PDF export needs jsPDF.\nPlease run: npm i jspdf\nThen rebuild and try again."
        );
        return;
      }

      const { jsPDF } = jsPDFmod;
      const doc = new jsPDF({ unit: "pt", format: "a4", compressPdf: true });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const marginX = 40;
      const marginY = 40;

      // Header
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text(
        `${projectName || `Project ${projectId || ""}`} — Treasurer Bills`,
        marginX,
        marginY
      );
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text(
        `Totals — Income: LKR ${currency(totals.income)}  |  Expense: LKR ${currency(
          totals.expense
        )}  |  Net: LKR ${currency(totals.net)}`,
        marginX,
        marginY + 18
      );

      const grouped = [
        { title: "Income", items: incomes },
        { title: "Expense", items: expenses },
      ];

      let y = marginY + 40;
      for (const group of grouped) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(group.title, marginX, y);
        y += 10;
        doc.setFont("helvetica", "normal");

        let slot = 0; // 0 or 1 (two per page)
        const slotHeight = (pageH - marginY * 2 - 20) / 2;
        const imgMaxW = pageW - marginX * 2;
        const imgMaxH = slotHeight - 80;

        for (const e of group.items) {
          if (slot === 2) {
            doc.addPage();
            y = marginY;
            slot = 0;
          }

          if (slot === 0) {
            // top slot
          } else if (slot === 1) {
            y = marginY + 20 + slotHeight;
          }

          doc.setFontSize(11);
          doc.setTextColor(0, 0, 0);
          doc.text(
            `${e.date}  •  ${e.description || "-"}  •  LKR ${currency(e.amount)}`,
            marginX,
            y
          );
          y += 12;

          let dataUrl = null;
          if (!e.saved && e.file && isLikelyImage(e.file.name, e.file.type)) {
            dataUrl = await fileToDataUrl(e.file);
          } else if (e.saved && e.bill_url) {
            if (isLikelyImage(e.bill_filename, e.bill_mime)) {
              dataUrl = await urlToImageDataUrl(e.bill_url);
            } else {
              dataUrl = await urlToImageDataUrl(e.bill_url);
            }
          }

          if (dataUrl) {
            doc.addImage(
              dataUrl,
              "JPEG",
              marginX,
              y,
              imgMaxW,
              imgMaxH,
              undefined,
              "FAST"
            );
            y += imgMaxH + 6;
          } else {
            const label = e.bill_filename
              ? `Bill file: ${e.bill_filename}`
              : "Bill file (not an image)";
            doc.setFontSize(10);
            doc.setTextColor(80, 80, 80);
            const lines = doc.splitTextToSize(label, pageW - marginX * 2);
            doc.text(lines, marginX, y);
            y += Math.min(30, lines.length * 12);
            doc.setTextColor(0, 0, 0);
          }

          const desc = e.description || "";
          if (desc) {
            const lines = doc.splitTextToSize(desc, pageW - marginX * 2);
            doc.text(lines, marginX, y);
            y += Math.min(40, lines.length * 12);
          }

          slot += 1;
          if (slot === 2) {
            y = pageH - marginY; // will trigger addPage on next iteration
          } else {
            y = marginY + 20 + slotHeight;
          }
        }

        if (group !== grouped[grouped.length - 1]) {
          doc.addPage();
          y = marginY;
        }
      }

      doc.save(`treasurer_bills_${projectId || "report"}.pdf`);
    } catch (e) {
      console.error("PDF export error:", e);
      alert(e?.message || "Failed to export PDF.");
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Sidebar />
      <Topbar
        user={{
          name: user?.name || "Treasurer",
          role: "Report",
          image: user?.image || "/user.png",
        }}
      />

      <main className="pl-0 lg:pl-64 mt-14 pb-10">
        <div className="mx-auto w-full max-w-screen-2xl px-3 sm:px-5 lg:px-10">
          {/* Back link */}
          <Link
            to={projectId ? `/project/${projectId}` : `/club-projects`}
            className="btn btn-ghost btn-sm"
            style={{ width: "fit-content", marginBottom: "var(--sp-4)" }}
          >
            <FaArrowLeft size={12} />
            <span>Back</span>
          </Link>

          {/* Page head */}
          <div className="page-head">
            <div>
              <div className="eyebrow">Treasurer Report</div>
              <h1>
                {projectName || (projectId ? `Project ${projectId}` : "Treasurer Report")}
                {projectId ? ` — Phase ${projectPhase || 1}` : ""}
              </h1>
              <p className="page-desc">
                {projectId
                  ? "Track income, expenses, and attach bills."
                  : "Missing/invalid Project ID in URL."}
              </p>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <button onClick={exportCSV} className="btn btn-ghost btn-sm" type="button">
                <FaFileCsv size={13} /> Export CSV
              </button>

              <button onClick={exportExcel} className="btn btn-ghost btn-sm" type="button">
                <FaFileExcel size={13} /> Export Excel
              </button>

              <button
                onClick={exportPDF}
                disabled={exportingPDF}
                className="btn btn-ghost btn-sm"
                type="button"
              >
                {exportingPDF ? <span className="spinner" aria-hidden="true" /> : <FaFilePdf size={13} />}
                {exportingPDF ? "Building PDF…" : "Export PDF"}
              </button>

              <button
                onClick={saveAll}
                disabled={
                  savingAll || !entries.some((e) => !e.saved) || !projectId
                }
                title={
                  !projectId
                    ? "Invalid project ID in URL"
                    : entries.some((e) => !e.saved)
                    ? "Save unsaved rows"
                    : "No new rows"
                }
                className="btn btn-primary btn-sm"
                type="button"
              >
                {savingAll ? <span className="spinner" aria-hidden="true" /> : <FaSave size={13} />}
                {savingAll ? "Saving…" : "Save All"}
              </button>
            </div>
          </div>


          {!projectId && (
            <div className="warning-banner" style={{ marginBottom: "var(--sp-6)" }}>
              <span>
                Use a URL like{" "}
                <code style={{ background: "rgba(0,0,0,.3)", padding: "1px 5px", borderRadius: 4 }}>
                  /project/123/tresurer
                </code>{" "}
                or define a route param named <code>projectId</code> or <code>id</code>.
              </span>
            </div>
          )}

          {/* Totals */}
          <div className="priority">
            <div className="panel hero-metric">
              <div className="hero-top">
                <span className="tile-label">Net Balance</span>
                <FaBalanceScale size={16} style={{ color: "var(--text-3)" }} />
              </div>
              <div className="hero-value num">LKR {currency(totals.net)}</div>
              <div className={`tile-trend ${totals.net >= 0 ? "trend-up" : "trend-warn"}`}>
                {totals.net >= 0 ? <FaArrowUp size={11} /> : <FaArrowDown size={11} />}
                <span>{totals.net >= 0 ? "Positive balance" : "Negative balance"}</span>
              </div>
            </div>

            <div className="panel tile">
              <div className="tile-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <FaArrowUp size={11} style={{ color: "var(--success)" }} />
                <span>Total Income</span>
              </div>
              <div className="tile-value num">LKR {currency(totals.income)}</div>
            </div>

            <div className="panel tile">
              <div className="tile-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <FaArrowDown size={11} style={{ color: "var(--error)" }} />
                <span>Total Expense</span>
              </div>
              <div className="tile-value num">LKR {currency(totals.expense)}</div>
            </div>
          </div>

          {/* Search */}
          <div className="panel" style={{ padding: "var(--sp-2) var(--sp-4)", marginBottom: "var(--sp-6)" }}>
            <div className="filter-row">
              <FaSearch size={13} style={{ color: "var(--text-3)", flexShrink: 0 }} />
              <label htmlFor="txn-search" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)" }}>
                Search entries by description or type
              </label>
              <input
                id="txn-search"
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search description or type…"
                style={{ border: "none", background: "transparent", flex: 1, padding: "8px 0" }}
              />
            </div>
          </div>


          {/* Add Entry Form */}
          <section className="panel" style={{ marginBottom: "var(--sp-6)" }}>
            <div className="section-head">
              <h2>Add Entry</h2>
            </div>

            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4"
              style={{ padding: "0 var(--sp-4) var(--sp-4)" }}
            >
              {/* type */}
              <div className="field lg:col-span-2" style={{ marginBottom: 0 }}>
                <label className="field-label" htmlFor="txn-type">Type</label>
                <select
                  id="txn-type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  <option value="income">Income</option>
                  <option value="expense">Expense</option>
                </select>
              </div>

              {/* amount */}
              <div className="field lg:col-span-2" style={{ marginBottom: 0 }}>
                <label className="field-label" htmlFor="txn-amount">Amount (LKR)</label>
                <input
                  id="txn-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                />
              </div>

              {/* date */}
              <div className="field lg:col-span-2" style={{ marginBottom: 0 }}>
                <label className="field-label" htmlFor="txn-date">Date</label>
                <input
                  id="txn-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>

              {/* description */}
              <div className="field lg:col-span-3" style={{ marginBottom: 0 }}>
                <label className="field-label" htmlFor="txn-desc">Description</label>
                <input
                  id="txn-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description"
                />
              </div>

              {/* file */}
              <div className="field lg:col-span-3" style={{ marginBottom: 0 }}>
                <label className="field-label" htmlFor="txn-file">Bill attachment (optional)</label>
                <input
                  id="txn-file"
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  onChange={async (e) => {
                    const original = e.target.files?.[0] || null;
                    setCompressionInfo(null);
                    if (!original) {
                      setFile(null);
                      if (selectedPreviewUrl) URL.revokeObjectURL(selectedPreviewUrl);
                      setSelectedPreviewUrl("");
                      return;
                    }
                    setCompressing(true);
                    const compressed = await compressBillImage(original);
                    setCompressing(false);
                    if (compressed !== original) {
                      setCompressionInfo({ originalSize: original.size, compressedSize: compressed.size });
                    }
                    setFile(compressed);
                    if (selectedPreviewUrl) URL.revokeObjectURL(selectedPreviewUrl);
                    setSelectedPreviewUrl(URL.createObjectURL(compressed));
                  }}
                  style={{
                    background: "var(--surface-2)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-sm)",
                    padding: "9px 12px",
                    color: "var(--text-2)",
                    fontSize: 13,
                  }}
                />
              </div>

              {(compressing || compressionInfo) && (
                <div
                  className="lg:col-span-3 field-hint"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    color: compressing ? "var(--link)" : "var(--success)",
                  }}
                >
                  {compressing && <span className="spinner" aria-hidden="true" />}
                  <span>
                    {compressing
                      ? "Compressing image…"
                      : `Compressed ${formatBytes(compressionInfo.originalSize)} → ${formatBytes(compressionInfo.compressedSize)}`}
                  </span>
                </div>
              )}

              {/* buttons */}
              <div className="lg:col-span-6" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  onClick={addEntry}
                  disabled={!amount || Number(amount) <= 0 || compressing}
                  className="btn btn-primary"
                  type="button"
                >
                  <FaPlus size={12} /> Add Entry
                </button>

                <button type="button" onClick={resetForm} className="btn btn-ghost">
                  <FaRedo size={12} /> Clear
                </button>
              </div>
            </div>

            {/* Selected file info + PREVIEW link */}
            {file && (
              <div className="field-hint" style={{ padding: "0 var(--sp-4) var(--sp-4)" }}>
                <span>Selected bill: </span>
                <span style={{ color: "var(--text-1)" }}>{file.name}</span>
                {selectedPreviewUrl && (
                  <a
                    style={{
                      color: "var(--link)",
                      textDecoration: "underline",
                      marginLeft: 8,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                    href={selectedPreviewUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FaExternalLinkAlt size={10} /> Preview
                  </a>
                )}
              </div>
            )}
          </section>


          {/* Income */}
          <section className="panel" style={{ marginBottom: "var(--sp-6)" }}>
            <div className="section-head">
              <h2>Income</h2>
            </div>

            {loading ? (
              <div style={{ display: "grid", gap: 10, padding: "0 var(--sp-4) var(--sp-4)" }}>
                <div className="skeleton" style={{ height: 20, width: 160 }} />
                <div className="skeleton" style={{ height: 20, width: "50%" }} />
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Date</th>
                      <th scope="col">Description</th>
                      <th scope="col" style={{ textAlign: "right" }}>Amount (LKR)</th>
                      <th scope="col">Bill</th>
                      <th scope="col" style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {incomes.map((e) => {
                      const key = e.saved ? `s-${e.id}` : `t-${e.tempId}`;
                      const previewHref = e.saved ? (e.bill_url || "") : (e.billLocalUrl || "");
                      const hasPreview = Boolean(previewHref);
                      return (
                        <tr key={key}>
                          <td className="num" style={{ whiteSpace: "nowrap" }}>{e.date}</td>
                          <td style={{ maxWidth: 520 }}>
                            <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                              {e.description}
                            </span>
                          </td>
                          <td className="num" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                            {currency(e.amount)}
                          </td>
                          <td>
                            {hasPreview ? (
                              <a
                                href={previewHref}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  color: "var(--link)",
                                  textDecoration: "underline",
                                  fontSize: 12,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <FaExternalLinkAlt size={10} /> Preview
                              </a>
                            ) : (
                              <span style={{ color: "var(--text-3)", fontSize: 12 }}>—</span>
                            )}
                          </td>
                          <td>
                            <div className="row-actions">
                              <button
                                onClick={() => deleteEntry(e)}
                                disabled={savingAll}
                                className="icon-btn"
                                aria-label={`Delete income entry from ${e.date}`}
                                title="Delete entry"
                                type="button"
                              >
                                <FaTrash size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    {!incomes.length && (
                      <tr>
                        <td colSpan={5}>
                          <div className="empty-state">
                            <div className="empty-icon"><FaInbox size={18} /></div>
                            <h3>No income records yet</h3>
                            <p>Add an entry above to start tracking income for this project.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>

{/* Expense */}
          <section className="panel" style={{ marginBottom: "var(--sp-6)" }}>
            <div className="section-head">
              <h2>Expense</h2>
            </div>

            {loading ? (
              <div style={{ display: "grid", gap: 10, padding: "0 var(--sp-4) var(--sp-4)" }}>
                <div className="skeleton" style={{ height: 20, width: 160 }} />
                <div className="skeleton" style={{ height: 20, width: "50%" }} />
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Date</th>
                      <th scope="col">Description</th>
                      <th scope="col" style={{ textAlign: "right" }}>Amount (LKR)</th>
                      <th scope="col">Bill</th>
                      <th scope="col" style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {expenses.map((e) => {
                      const key = e.saved ? `s-${e.id}` : `t-${e.tempId}`;
                      const previewHref = e.saved ? (e.bill_url || "") : (e.billLocalUrl || "");
                      const hasPreview = Boolean(previewHref);
                      return (
                        <tr key={key}>
                          <td className="num" style={{ whiteSpace: "nowrap" }}>{e.date}</td>
                          <td style={{ maxWidth: 520 }}>
                            <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                              {e.description}
                            </span>
                          </td>
                          <td className="num" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                            {currency(e.amount)}
                          </td>
                          <td>
                            {hasPreview ? (
                              <a
                                href={previewHref}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  color: "var(--link)",
                                  textDecoration: "underline",
                                  fontSize: 12,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <FaExternalLinkAlt size={10} /> Preview
                              </a>
                            ) : (
                              <span style={{ color: "var(--text-3)", fontSize: 12 }}>—</span>
                            )}
                          </td>
                          <td>
                            <div className="row-actions">
                              <button
                                onClick={() => deleteEntry(e)}
                                disabled={savingAll}
                                className="icon-btn"
                                aria-label={`Delete expense entry from ${e.date}`}
                                title="Delete entry"
                                type="button"
                              >
                                <FaTrash size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    {!expenses.length && (
                      <tr>
                        <td colSpan={5}>
                          <div className="empty-state">
                            <div className="empty-icon"><FaInbox size={18} /></div>
                            <h3>No expense records yet</h3>
                            <p>Add an entry above to start tracking expenses for this project.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
